import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Button,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const AssignReasonModal = ({
  open,
  onClose,
  onBack,
  onSubmit,
  assignType, // 'assign' or 'reassign'
  isUpgrade = false,
  selectedVehicle = null,
  tripDetails = null,
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [comments, setComments] = useState('');
  const [errors, setErrors] = useState({});

  const reasonsData = {
    assign: [
      { display_text: 'Driver Unavailable' },
      { display_text: 'Vehicle Breakdown / Mechanical Failure' },
      { display_text: 'Compliance issue ' },
      { display_text: 'No vehicles in booked category' },
      { display_text: 'Others' },
    ],
    reassign: [
      { display_text: 'Driver Unavailable' },
      { display_text: 'Vehicle Breakdown / Mechanical Failure' },
      { display_text: 'Compliance issue ' },
      { display_text: 'No vehicles in booked category' },
      { display_text: 'Others' },
    ],
    upgrade: [
      { display_text: 'Driver Cancelled' },
      { display_text: 'Vehicle Breakdown / Mechanical Failure' },
      { display_text: 'Operational Priority (VIP / Urgent Requirement)' },
      { display_text: 'Goodwill Upgrade' },
      { display_text: 'Others' },
    ],
  };

  const getCurrentReasons = () => {
    if (isUpgrade) return reasonsData.upgrade;
    return reasonsData[assignType] || reasonsData.assign;
  };

  const getPlaceholder = () => {
    if (isUpgrade) return "Select reason for vehicle's category change";
    return assignType === 'reassign' ? 'Select reason for reassign' : 'Select reason for assign';
  };

  const getModalTitle = () => {
    if (isUpgrade) return "Vehicle's Category Change Reason";
    return assignType === 'reassign' ? 'Reassign Reason' : 'Assign Reason';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedReason) {
      newErrors.reason = 'Please select a reason';
    }

    if (selectedReason === 'Others' && !comments.trim()) {
      newErrors.comments = 'Comments are required when "Others" is selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit({
        reason: selectedReason,
        comments: comments.trim(),
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setComments('');
    setErrors({});
    onClose();
  };

  const handleBackClick = () => {
    setSelectedReason('');
    setComments('');
    setErrors({});
    onBack();
  };

  // Check if this is an upgrade scenario based on carTypeId mismatch
  const checkIsUpgrade = () => {
    if (!selectedVehicle || !tripDetails) return false;
    return selectedVehicle?.Vehicle?.carTypeId !== tripDetails?.carTypeId;
  };

  const shouldShowUpgradeWarning = isUpgrade || checkIsUpgrade();

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="assign-reason-modal"
      className="customModal"
    >
      <Box
        className="modalContent"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 600 },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <Box
          className="closeIcon justifyBetween"
          sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton aria-label="back" onClick={handleBackClick} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography className="colorPrimary fw600 font24" component="h2">
              {getModalTitle()}
            </Typography>
          </Box>
          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {shouldShowUpgradeWarning && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Are you sure you want to change the vehicle's category?
          </Alert>
        )}

        <Box sx={{ mt: 3 }}>
          <Box sx={{ mb: '24px' }}>
            <TextField
              select
              fullWidth
              label={getPlaceholder()}
              value={selectedReason}
              onChange={(e) => {
                setSelectedReason(e.target.value);
                if (errors.reason) {
                  setErrors({ ...errors, reason: '' });
                }
                if (e.target.value !== 'Others') {
                  setErrors({ ...errors, comments: '' });
                }
              }}
              error={!!errors.reason}
              helperText={errors.reason}
              required
            >
              {getCurrentReasons().map((reason, index) => (
                <MenuItem key={index} value={reason.display_text}>
                  {reason.display_text}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Comments"
              placeholder="Enter your comments here"
              value={comments}
              onChange={(e) => {
                setComments(e.target.value);
                if (errors.comments) {
                  setErrors({ ...errors, comments: '' });
                }
              }}
              error={!!errors.comments}
              helperText={errors.comments}
              required={selectedReason === 'Others'}
              sx={{ mb: 3 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleBackClick}
              sx={{
                minWidth: 100,
                color: '#969696',
                border: 'none',
                textTransform: 'capitalize',
                ':hover': { color: '#969696', border: 'none' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              sx={{
                minWidth: 100,
                backgroundColor: 'var(--brand-accent)',
                color: 'white',
                textTransform: 'capitalize',
                ':hover': { backgroundColor: 'var(--brand-accent)' },
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AssignReasonModal;
